import { useState, useEffect, useRef, useMemo } from 'react';
import api from '../../services/api';
import {
  Building2, RefreshCw, Play, Pause, Clock, TrendingUp, ArrowDownCircle,
  ArrowUpCircle, Gift, Users,
} from 'lucide-react';

const PERIODS = [
  { id: 'day', label: 'Today' },
  { id: 'week', label: '7 Days' },
  { id: 'month', label: '30 Days' },
  { id: '90d', label: '90 Days' },
];
const POLL_MS = 2000;

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString();
const fmtFP = (v) => `${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} FP`;

/**
 * Tenant Activity — master-only dashboard.
 *
 * One card per tenant (and one synthetic "Main" scope) showing the same KPIs
 * as the Live Insights page, but per-tenant so the platform owner can see at
 * a glance which tenants are growing and which are quiet.
 */
export default function TenantActivityPage() {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [liveOn, setLiveOn] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const pollRef = useRef(null);

  const fetchData = async (p = period, silent = false) => {
    if (!silent) setLoading(true);
    setIsFetching(true);
    try {
      const res = await api.get(`/api/admin/analytics/tenant-activity?period=${p}`);
      setData(res.data);
      setLastUpdated(new Date());
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.response?.data?.message || 'Failed to load tenant activity');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  useEffect(() => { fetchData(period); /* eslint-disable-next-line */ }, [period]);

  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!liveOn) return;
    pollRef.current = setInterval(() => { fetchData(period, true); }, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, [liveOn, period]);

  const tenants = data?.tenants || [];
  const totals = data?.totals || {};

  // Enriched rows with percent-of-total metrics for the bar visualisation
  const enriched = useMemo(() => {
    const max = tenants.reduce((m, r) => Math.max(m, r.period.deposit_amount, r.period.withdraw_amount, r.period.new_signups), 0) || 1;
    return tenants.map(t => ({ ...t, _max: max }));
  }, [tenants]);

  return (
    <div className="space-y-5" data-testid="tenant-activity-page">
      {/* Header + controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#D4AF37]" /> Tenant Activity
          </h2>
          <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
            <Clock className="w-3.5 h-3.5" />
            {lastUpdated ? `Last updated ${lastUpdated.toLocaleTimeString()}` : 'Loading…'}
            {liveOn && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-green-500/10 text-green-400 text-[10px] font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" /> LIVE
            </span>}
            {isFetching && <RefreshCw className="w-3.5 h-3.5 animate-spin text-gray-500" />}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-xl bg-[#0B0E14] border border-white/10 p-1" data-testid="ta-period-filter">
            {PERIODS.map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  period === p.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`ta-period-${p.id}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setLiveOn(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition ${
              liveOn ? 'bg-green-500/10 text-green-400 border-green-500/30' : 'bg-[#0B0E14] text-gray-400 border-white/10'
            }`}
            data-testid="ta-live-toggle"
          >
            {liveOn ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
            {liveOn ? 'Pause' : 'Live'}
          </button>
          <button
            onClick={() => fetchData(period)}
            className="p-2 rounded-lg bg-[#0B0E14] border border-white/10 text-gray-400 hover:text-white transition"
            data-testid="ta-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {err && <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{err}</div>}

      {/* Aggregate totals strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="ta-totals">
        <TotalTile icon={Building2} color="#D4AF37" label="Active Tenants" value={`${fmtInt(totals.active_tenants_in_period)} / ${fmtInt(totals.tenants)}`} />
        <TotalTile icon={ArrowDownCircle} color="#22c55e" label="Combined Deposits" value={fmtUSD(totals.combined_deposit_amount)} />
        <TotalTile icon={ArrowUpCircle} color="#ef4444" label="Combined Withdrawals" value={fmtUSD(totals.combined_withdraw_amount)} />
        <TotalTile icon={Users} color="#3B82F6" label="New Sign-ups" value={fmtInt(totals.combined_signups)} />
        <TotalTile icon={Users} color="#8b5cf6" label="Total Players" value={fmtInt(totals.combined_players)} />
      </div>

      {/* Per-tenant cards */}
      {loading && !data ? (
        <div className="flex items-center justify-center h-64"><div className="spinner" /></div>
      ) : enriched.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 p-10 text-center text-gray-500">No tenants configured yet.</div>
      ) : (
        <div className="space-y-3">
          {enriched.map(t => (
            <TenantRow
              key={t.tenant_id}
              tenant={t}
              period={period}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TotalTile({ icon: Icon, color, label, value }) {
  return (
    <div className="bg-[#131822] rounded-2xl border border-white/10 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-lg font-bold text-white tabular-nums">{value}</p>
    </div>
  );
}

function TenantRow({ tenant, period }) {
  const color = tenant.primary_color || '#D4AF37';
  const maxVal = tenant._max;
  const pct = (v) => (maxVal ? Math.min(100, (v / maxVal) * 100) : 0);

  const periodLabel = PERIODS.find(p => p.id === period)?.label || period;

  return (
    <div
      className="rounded-2xl p-4 bg-[#131822] border border-white/10 hover:border-white/20 transition"
      data-testid={`tenant-row-${tenant.slug || 'main'}`}
    >
      {/* Top: tenant identity + lifetime + quick actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm" style={{ background: `${color}22`, color }}>
            {(tenant.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
          </div>
          <div className="min-w-0">
            <p className="text-base font-semibold text-white truncate">
              {tenant.name}
              {tenant.is_main_scope && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-300">Main</span>}
            </p>
            <p className="text-[11px] text-gray-500">
              /{tenant.slug || '-'} · {fmtInt(tenant.lifetime.player_count)} lifetime players
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-gray-500">Lifetime:</span>
          <span className="text-green-400 font-semibold tabular-nums">{fmtUSD(tenant.lifetime.deposit_amount)}</span>
          <span className="text-gray-500">in</span>
          <span className="text-red-400 font-semibold tabular-nums">{fmtUSD(tenant.lifetime.withdraw_amount)}</span>
          <span className="text-gray-500">out</span>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
        <KPITile
          color="#22c55e" icon={ArrowDownCircle} label="Deposits" sub={periodLabel}
          primary={fmtUSD(tenant.period.deposit_amount)} secondary={`${fmtInt(tenant.period.deposit_count)} txns`}
          testid={`ta-deposits-${tenant.slug || 'main'}`}
        />
        <KPITile
          color="#ef4444" icon={ArrowUpCircle} label="Withdrawals" sub={periodLabel}
          primary={fmtUSD(tenant.period.withdraw_amount)} secondary={`${fmtInt(tenant.period.withdraw_count)} txns`}
          testid={`ta-withdrawals-${tenant.slug || 'main'}`}
        />
        <KPITile
          color={tenant.period.net_revenue >= 0 ? '#22c55e' : '#ef4444'} icon={TrendingUp}
          label="Net Revenue" sub="dep − with"
          primary={fmtUSD(tenant.period.net_revenue)}
          testid={`ta-net-${tenant.slug || 'main'}`}
        />
        <KPITile
          color="#3B82F6" icon={Users} label="New Sign-ups" sub={periodLabel}
          primary={fmtInt(tenant.period.new_signups)}
          testid={`ta-signups-${tenant.slug || 'main'}`}
        />
        <KPITile
          color="#D4AF37" icon={Gift} label="Bonuses Given" sub={periodLabel}
          primary={fmtFP(tenant.period.bonus_amount)}
          secondary={`${fmtInt(tenant.period.bonus_count)} grants`}
          testid={`ta-bonus-${tenant.slug || 'main'}`}
        />
        <KPITile
          color="#eab308" icon={Clock} label="Pending Queues"
          primary={`${fmtInt(tenant.pending.deposit_count)} · ${fmtInt(tenant.pending.withdraw_count)}`}
          secondary={`${fmtUSD(tenant.pending.deposit_amount)} / ${fmtUSD(tenant.pending.withdraw_amount)}`}
          testid={`ta-pending-${tenant.slug || 'main'}`}
          urgent={(tenant.pending.deposit_count + tenant.pending.withdraw_count) > 0}
        />
      </div>

      {/* Comparison bars */}
      <div className="mt-3 space-y-1.5">
        <BarLine label="Deposits" value={tenant.period.deposit_amount} pct={pct(tenant.period.deposit_amount)} color="#22c55e" formatter={fmtUSD} />
        <BarLine label="Withdrawals" value={tenant.period.withdraw_amount} pct={pct(tenant.period.withdraw_amount)} color="#ef4444" formatter={fmtUSD} />
        <BarLine label="New Sign-ups" value={tenant.period.new_signups} pct={pct(tenant.period.new_signups)} color="#3B82F6" formatter={fmtInt} />
      </div>
    </div>
  );
}

function KPITile({ icon: Icon, color, label, sub, primary, secondary, onClick, testid, urgent }) {
  const clickable = typeof onClick === 'function';
  return (
    <div
      className={`rounded-xl p-3 border transition ${urgent ? 'border-yellow-500/35' : 'border-white/5'} bg-white/[0.02] ${
        clickable ? 'cursor-pointer hover:border-[#D4AF37]/40 hover:bg-white/[0.04] active:scale-[0.99]' : ''
      }`}
      data-testid={testid}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <p className="text-[9px] text-gray-500 uppercase tracking-wider truncate">{label}</p>
      </div>
      <p className="text-sm font-bold text-white tabular-nums truncate" title={primary}>{primary}</p>
      <p className="text-[9px] text-gray-500 truncate">{secondary || sub}</p>
    </div>
  );
}

function BarLine({ label, value, pct, color, formatter }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[10px] text-gray-500">
        <span>{label}</span>
        <span className="tabular-nums text-gray-300">{formatter(value)}</span>
      </div>
      <div className="h-1 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color, opacity: 0.85 }} />
      </div>
    </div>
  );
}
